// Sidebar.js (updated)
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TopBar from './TopBar';

const NavItem = ({ icon, label, path, active, onClick }) => (
  <div onClick={onClick} style={{
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
    background: active ? '#eff6ff' : 'transparent',
    color: active ? '#1d4ed8' : '#64748b',
    fontSize: 14, fontWeight: active ? 600 : 400,
    transition: 'all 0.15s', marginBottom: 2,
    borderLeft: active ? '3px solid #2563eb' : '3px solid transparent',
  }}
    onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#374151'; }}}
    onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}}
  >
    <span style={{ fontSize: 17, minWidth: 20, textAlign: 'center' }}>{icon}</span>
    <span>{label}</span>
  </div>
);

export default function Sidebar() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const previousPaddingTop = document.body.style.paddingTop;
    document.body.style.paddingTop = '52px';
    const previousPaddingLeft = document.body.style.paddingLeft;
    document.body.style.paddingLeft = '248px';
    return () => {
      document.body.style.paddingTop = previousPaddingTop;
      document.body.style.paddingLeft = previousPaddingLeft;
    };
  }, []);

  const adminNav = [
    { icon: '🏠', label: 'Dashboard', path: '/admin/dashboard' },
    { icon: '💰', label: 'Cost Overview', path: '/admin/costs' },
    { icon: '⚙️', label: 'Services', path: '/admin/services' },
    { icon: '🧾', label: 'Invoices', path: '/admin/invoices' },
    { icon: '▦', label: 'Utilization', path: '/admin/utilization' },
    { icon: 'SKU', label: 'Google Usage', path: '/admin/google-usage' },
    { icon: '🎯', label: 'Budgets & Alerts', path: '/admin/budgets' },
    { icon: '👥', label: 'Manage Users', path: '/admin/users' },
  ];

  const clientNav = [
    { icon: '🏠', label: 'Dashboard', path: '/dashboard' },
    { icon: '💰', label: 'Cost Summary', path: '/costs' },
    { icon: '▦', label: 'Google Usage', path: '/google-usage' },
    { icon: '👥', label: 'Users', path: '/users' },
  ];

  const navItems = isAdmin ? adminNav : clientNav;

  return (
    <>
      <TopBar />
      <div style={s.sidebar}>
        {/* Logo */}
        <div style={s.logoWrap}>
          <img src="/ishan-logo.webp" alt="Ishan Technologies" style={s.logo} />
          <div style={s.logoLine} />
          <div style={s.appLabel}>Ishan Cloud Billing</div>
        </div>

        {/* Role badge */}
        <div style={{ 
          ...s.roleBadge, 
          background: isAdmin ? '#fef9c3' : '#dbeafe', 
          color: isAdmin ? '#854d0e' : '#1d4ed8', 
          border: `1px solid ${isAdmin ? '#fde047' : '#bfdbfe'}` 
        }}>
          {isAdmin ? '👑 Administrator' : '👤 Client Account'}
        </div>

        {/* Nav */}
        <nav style={s.nav}>
          <p style={s.navSection}>NAVIGATION</p>
          {navItems.map(item => (
            <NavItem 
              key={item.path} 
              {...item} 
              active={location.pathname === item.path} 
              onClick={() => navigate(item.path)} 
            />
          ))}
        </nav>

        {/* User footer */}
        <div style={s.footer}>
          <div style={s.avatar}>{user?.name?.[0]?.toUpperCase()}</div>
          <div style={s.userInfo}>
            <div style={s.userName}>{user?.name}</div>
            <div style={s.userEmail}>{user?.email}</div>
          </div>
          <button onClick={logout} style={s.logoutBtn} title="Sign out">↩</button>
        </div>
      </div>
    </>
  );
}

const s = {
  sidebar: {
    width: 248, 
    minHeight: '100vh', 
    background: '#ffffff',
    borderRight: '1px solid #e2e8f0', 
    display: 'flex', 
    flexDirection: 'column',
    padding: '20px 16px', // Reduced top padding since no extra space needed
    position: 'fixed', 
    left: 0, 
    top: 0, 
    bottom: 0,
    overflowY: 'auto', 
    fontFamily: "'Inter','Segoe UI',sans-serif",
    boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
    zIndex: 101,
  },
  logoWrap: { marginBottom: 20, marginTop: 0 }, // Reset margin top
  logo: { height: 36, objectFit: 'contain', objectPosition: 'left', display: 'block', maxWidth: '100%', marginBottom: 12 },
  logoLine: { height: 1, background: '#e2e8f0', marginBottom: 8 },
  appLabel: { color: '#94a3b8', fontSize: 11, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' },
  roleBadge: { padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, marginBottom: 20, textAlign: 'center' },
  nav: { flex: 1 },
  navSection: { color: '#cbd5e1', fontSize: 10, fontWeight: 700, letterSpacing: '1px', marginBottom: 6, marginTop: 4, paddingLeft: 4 },
  footer: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', marginTop: 12 },
  avatar: { width: 34, height: 34, borderRadius: 8, background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0 },
  userInfo: { flex: 1, overflow: 'hidden' },
  userName: { color: '#0f172a', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userEmail: { color: '#94a3b8', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  logoutBtn: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 7, cursor: 'pointer', width: 30, height: 30, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' },
};